import React, { useEffect, useRef, useState } from 'react';
import {
  IoAddOutline,
  IoAlertCircleOutline,
  IoChevronDown,
  IoChevronForward,
  IoCloudUploadOutline,
  IoDocumentsOutline,
  IoEllipsisHorizontal,
  IoFileTrayOutline,
  IoFolderOpenOutline,
  IoFolderOutline,
  IoGitMergeOutline,
  IoHomeOutline,
  IoMailOutline,
  IoPencilOutline,
  IoReturnUpForwardOutline,
  IoShareOutline,
  IoSparklesOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { folderBadge } from './fileLibrary';

export const NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: IoHomeOutline },
  { id: 'all', label: 'All files', icon: IoDocumentsOutline },
  { id: 'uploaded', label: 'Uploaded', icon: IoCloudUploadOutline },
  { id: 'generated', label: 'Generated', icon: IoSparklesOutline },
  { id: 'email_research', label: 'Email & research', icon: IoMailOutline },
  { id: 'unfiled', label: 'Unfiled', icon: IoFileTrayOutline },
];

function navCount(id, stats, unfiledCount) {
  if (!stats) return id === 'unfiled' && unfiledCount != null ? unfiledCount : null;
  const groups = stats.by_source_group || {};
  if (id === 'all') return stats.total_files;
  if (id === 'uploaded') return groups.uploaded;
  if (id === 'generated') return groups.generated;
  if (id === 'email_research') return (Number(groups.email) || 0) + (Number(groups.research) || 0);
  if (id === 'unfiled') return stats.unfiled_count ?? unfiledCount;
  return null;
}

function FolderMenu({ folder, actions, onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) onDone(); };
    const focusTrigger = () => ref.current?.closest('.fl-tree-row')?.querySelector('.fl-tree-more')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        focusTrigger();
        onDone();
      }
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    ref.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onDone]);

  const item = (label, Icon, run, { danger = false } = {}) => (
    <button
      type="button"
      role="menuitem"
      className={`fl-menu-item${danger ? ' is-danger' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        // The menu is about to unmount: park focus on its "…" button so a
        // dialog opened from here can hand focus back to it.
        e.currentTarget.closest('.fl-tree-row')?.querySelector('.fl-tree-more')?.focus();
        onDone();
        run(folder);
      }}
    >
      <Icon size={14} aria-hidden="true" /> {label}
    </button>
  );

  return (
    <div className="fl-menu" role="menu" aria-label={`${folder.name} actions`} ref={ref} onClick={(e) => e.stopPropagation()}>
      {item('New subfolder', IoAddOutline, actions.createSubfolder)}
      {item('Rename', IoPencilOutline, actions.rename)}
      {item('Move to…', IoReturnUpForwardOutline, actions.move)}
      {item('Merge into…', IoGitMergeOutline, actions.merge)}
      {actions.share ? item('Send to company', IoShareOutline, actions.share) : null}
      <div className="fl-menu-divider" />
      {item('Delete', IoTrashOutline, actions.remove, { danger: true })}
    </div>
  );
}

/**
 * Left navigation: the fixed views, then the folder tree. Folder rows and the
 * Unfiled item are drop targets for files dragged from a list (desktop).
 */
export default function LibraryNav({
  view,
  onSelectView,
  folderIndex,
  treeState,
  onRetryTree,
  stats,
  expanded,
  onToggleFolder,
  folderActions,
  onCreateFolder,
  dragActive,
  dropTargetId,
  onDragOverTarget,
  onDragLeaveTarget,
  onDropOnTarget,
}) {
  const [menuFolderId, setMenuFolderId] = useState(null);
  const closeMenu = React.useCallback(() => setMenuFolderId(null), []);

  const dropProps = (targetKey, folderId) => (dragActive ? {
    onDragOver: (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      onDragOverTarget(targetKey);
    },
    onDragLeave: () => onDragLeaveTarget(targetKey),
    onDrop: (e) => {
      e.preventDefault();
      onDropOnTarget(folderId);
    },
  } : {});

  const renderFolder = (folder, depth) => {
    const kids = folderIndex.children.get(folder.folder_id) || [];
    const isOpen = Boolean(expanded[folder.folder_id]);
    const isActive = view.id === 'folder' && view.folderId === folder.folder_id;
    const badge = folderBadge(folder);
    const hint = folderIndex.hintOf(folder.folder_id);
    const count = Number(folder.file_count) || 0;
    const targetKey = `folder:${folder.folder_id}`;
    return (
      <li key={folder.folder_id} className="fl-tree-node">
        <div
          className={`fl-tree-row${isActive ? ' is-active' : ''}${dropTargetId === targetKey ? ' is-drop-target' : ''}`}
          style={{ paddingLeft: `${6 + depth * 14}px` }}
          {...dropProps(targetKey, folder.folder_id)}
        >
          {kids.length ? (
            <button
              type="button"
              className="fl-tree-toggle"
              onClick={() => onToggleFolder(folder.folder_id)}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${folder.name}`}
              aria-expanded={isOpen}
            >
              {isOpen ? <IoChevronDown size={13} /> : <IoChevronForward size={13} />}
            </button>
          ) : <span className="fl-tree-toggle-spacer" />}
          <button
            type="button"
            className="fl-tree-label"
            onClick={() => onSelectView({ id: 'folder', folderId: folder.folder_id })}
            aria-current={isActive ? 'page' : undefined}
            title={folderIndex.pathOf(folder.folder_id)}
          >
            {isActive ? <IoFolderOpenOutline size={15} className="fl-folder-glyph" aria-hidden="true" /> : <IoFolderOutline size={15} className="fl-folder-glyph" aria-hidden="true" />}
            <span className="fl-tree-name-wrap">
              <span className="fl-tree-name">{folder.name}</span>
              {hint ? <span className="fl-tree-hint">{hint}</span> : null}
            </span>
            {/* Most folders are Atlas-made, so the narrow tree only flags shared ones; cards show both. */}
            {badge && badge !== 'Atlas' ? <span className={`fl-badge fl-badge-${badge.toLowerCase()}`}>{badge}</span> : null}
            <span className="fl-tree-count" aria-label={`${count} files`}>{count}</span>
          </button>
          <button
            type="button"
            className={`fl-tree-more${menuFolderId === folder.folder_id ? ' is-open' : ''}`}
            onClick={() => setMenuFolderId(menuFolderId === folder.folder_id ? null : folder.folder_id)}
            aria-label={`More actions for ${folder.name}`}
            aria-haspopup="menu"
            aria-expanded={menuFolderId === folder.folder_id}
          >
            <IoEllipsisHorizontal size={14} />
          </button>
          {menuFolderId === folder.folder_id ? (
            <FolderMenu folder={folder} actions={folderActions} onDone={closeMenu} />
          ) : null}
        </div>
        {isOpen && kids.length ? (
          <ul className="fl-tree-children">{kids.map((child) => renderFolder(child, depth + 1))}</ul>
        ) : null}
      </li>
    );
  };

  const unfiledCount = navCount('unfiled', stats, treeState.unfiledCount);

  return (
    <nav className="fl-nav" aria-label="File library">
      <ul className="fl-nav-list">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = view.id === item.id;
          const count = item.id === 'unfiled' ? unfiledCount : navCount(item.id, stats);
          const isDrop = item.id === 'unfiled';
          return (
            <li key={item.id}>
              <button
                type="button"
                className={`fl-nav-item${isActive ? ' is-active' : ''}${isDrop && dropTargetId === 'unfiled' ? ' is-drop-target' : ''}`}
                onClick={() => onSelectView({ id: item.id })}
                aria-current={isActive ? 'page' : undefined}
                {...(isDrop ? dropProps('unfiled', null) : {})}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{item.label}</span>
                {count != null && count !== '' ? <span className="fl-nav-count">{count}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="fl-nav-section">
        <span>Folders</span>
        <button type="button" className="fl-icon-btn fl-icon-btn-sm" onClick={() => onCreateFolder(null)} aria-label="New folder" title="New folder">
          <IoAddOutline size={16} />
        </button>
      </div>

      <div className="fl-tree">
        {treeState.status === 'loading' && !folderIndex.roots.length ? (
          <div className="fl-tree-status"><span className="fl-spinner" aria-hidden="true" /> Loading folders…</div>
        ) : null}
        {treeState.status === 'error' ? (
          <div className="fl-inline-error fl-inline-error-compact" role="alert">
            <IoAlertCircleOutline size={15} aria-hidden="true" />
            <span>{treeState.error || 'Folders could not be loaded.'}</span>
            <button type="button" className="fl-link-btn" onClick={onRetryTree}>Retry</button>
          </div>
        ) : null}
        {treeState.status === 'ready' && !folderIndex.roots.length ? (
          <div className="fl-tree-status">No folders yet.</div>
        ) : null}
        {folderIndex.roots.length ? (
          <ul className="fl-tree-list">{folderIndex.roots.map((folder) => renderFolder(folder, 0))}</ul>
        ) : null}
      </div>
    </nav>
  );
}
